// Mag mascot — design handoff §3.5. Geometric black cat with a sparkle on
// top, yellow vertical-slit eyes, plum nose, and crescent-moon collar.
// SVG paths supplied by the design handoff verbatim — do not freelance.

import { Trans, useLingui } from '@lingui/react/macro';
import type { CSSProperties } from 'react';
import { useEffect, useRef } from 'react';

export type MagPalette = 'default' | 'inverted';

/**
 * Mag's mood — drives the eye rendering. Error-state design handoff §4 and
 * Home handoff §9/§15:
 *   - default  → normal slit-pupil eyes (everywhere else in the product)
 *   - napping  → closed eyes, curving downward; paired with floating "z z z"
 *   - casting  → eyes squinting upward; "i'm working on it" concentration
 *   - confused → asymmetric eyes (one slit + one round); puzzled
 *   - pounced  → wide round excited eyes; wins / celebrations
 *   - smug     → half-lidded horizontal slits; "told you so"
 * Mood is the signal, never the decoration — see §15 for the mood→state map.
 */
export type MagMood = 'default' | 'napping' | 'casting' | 'confused' | 'pounced' | 'smug';

export interface MagProps {
  size?: number;
  palette?: MagPalette;
  trackCursor?: boolean;
  className?: string;
  mood?: MagMood;
}

export function Mag({
  size = 140,
  palette = 'default',
  trackCursor = false,
  className,
  mood = 'default',
}: MagProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  // Default palette: ink body, spell eyes, plum nose, spell collar dot.
  // Inverted: spell body, ink eyes — used on ink backgrounds.
  const body = palette === 'default' ? '#0F0E18' : '#FFD018';
  const eyeFill = palette === 'default' ? '#FFD018' : '#0F0E18';
  const pupil = palette === 'default' ? '#0F0E18' : '#FFD018';
  const nose = '#421C6C';
  const collarOuter = palette === 'default' ? '#FFD018' : '#0F0E18';
  const collarInner = palette === 'default' ? '#0F0E18' : '#FFD018';

  useEffect(() => {
    if (!trackCursor || !svgRef.current) return;
    const svg = svgRef.current;
    let raf = 0;
    let mx = 0;
    let my = 0;
    const onMove = (e: MouseEvent) => {
      mx = e.clientX;
      my = e.clientY;
      if (!raf) raf = requestAnimationFrame(apply);
    };
    const apply = () => {
      raf = 0;
      const r = svg.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = Math.max(-2, Math.min(2, (mx - cx) / 90));
      const dy = Math.max(-2, Math.min(2, (my - cy) / 90));
      svg.style.setProperty('--eye-tx', `${dx.toFixed(2)}px`);
      svg.style.setProperty('--eye-ty', `${dy.toFixed(2)}px`);
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [trackCursor]);

  // Native aspect of the SVG is 186:267 (~0.7). `size` is the visual width;
  // height preserves aspect.
  const width = size;
  const height = Math.round(size * (267 / 186));

  // Napping Mag breathes gently — a barely-there scale loop on the whole
  // SVG sells the "asleep" read without distracting from the rest of the
  // page. Casting Mag sways like she's tracking a thread of thought; the
  // sparkles in the SVG fade/drift on top of the sway. The z z z's are
  // the same idea for napping.
  const animClass = mood === 'napping' ? 'mag-breathe' : mood === 'casting' ? 'mag-cast-sway' : '';
  const combinedClass = [className, animClass].filter(Boolean).join(' ');

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 186 267"
      width={width}
      height={height}
      className={combinedClass}
      aria-hidden="true"
    >
      <title>Mag</title>
      {/* Head + ears + sparkle-on-top — one combined path from the handoff. */}
      <path
        d="M180.714 49.9111L164.087 52.9238C177.516 69.02 185.598 89.7333 185.598 112.335C185.598 163.586 144.05 205.134 92.7988 205.134C41.5475 205.134 0 163.586 0 112.335C0.000102634 88.8196 8.74664 67.3469 23.1641 50.9932L9.76855 48.5625L34.4648 0L50.1133 29.916C62.8918 23.2845 77.4075 19.5361 92.7988 19.5361C109.496 19.5361 125.164 23.9471 138.7 31.666L155.291 0L180.714 49.9111Z"
        fill={body}
      />
      {/* Whiskers — three short horizontal strokes per cheek in spell yellow,
          sitting at the nose baseline. Part of Mag's identity; rendered in
          every mood (including napping — a sleeping cat still has whiskers). */}
      <g fill={eyeFill}>
        <rect x="18" y="140" width="34" height="2.6" rx="1.3" />
        <rect x="14" y="148" width="38" height="2.6" rx="1.3" />
        <rect x="18" y="156" width="34" height="2.6" rx="1.3" />
        <rect x="133" y="140" width="34" height="2.6" rx="1.3" />
        <rect x="133" y="148" width="38" height="2.6" rx="1.3" />
        <rect x="133" y="156" width="34" height="2.6" rx="1.3" />
      </g>
      {/* Eyes — swapped per mood. Coords sit on Mag's eye baseline (cy≈106.6),
          ±~13 from each eye center for the closed-arc widths. */}
      {mood === 'default' && (
        <>
          {/* Default eyes — narrower yellow + subtler slit so the face
              reads "calm and ready" instead of "surprised". Previous
              ellipses were rx=11.4 / ry=17.1, which on smaller renders
              made Mag look perpetually startled. Trimmed to rx=9.5 /
              ry=14.5 with proportionally narrower pupils. */}
          <g className="mag-eye">
            <ellipse cx="58.6097" cy="108" rx="9.5" ry="14.5" fill={eyeFill} />
            <ellipse cx="58.6101" cy="108" rx="2.6" ry="10.4" fill={pupil} />
          </g>
          <g className="mag-eye-2">
            <ellipse cx="126.988" cy="108" rx="9.5" ry="14.5" fill={eyeFill} />
            <ellipse cx="126.988" cy="108" rx="2.6" ry="10.4" fill={pupil} />
          </g>
          {/* Subtle smile — small upward arc below the nose. Sells the
              "content and listening" state without being cartoony. The
              smug mood has its own asymmetric smirk; default stays
              symmetric. */}
          <path
            d="M82 162 Q93 169 110 162"
            stroke={eyeFill}
            strokeWidth="2.4"
            fill="none"
            strokeLinecap="round"
            opacity="0.85"
          />
        </>
      )}
      {mood === 'napping' && (
        <>
          <g stroke={eyeFill} strokeWidth="3.6" fill="none" strokeLinecap="round">
            <path d="M46 105 Q58.6 122 71 105" />
            <path d="M114 105 Q127 122 140 105" />
          </g>
          {/* floating z z z to the right of the right ear — each on its
              own staggered fade/drift so it reads as breathing out a snore */}
          <g
            fill={eyeFill}
            fontFamily="'Geist', sans-serif"
            fontWeight={800}
            style={{ letterSpacing: '-0.04em' }}
          >
            <text x="150" y="62" fontSize="18" className="mag-snore mag-snore-1">
              <Trans>z</Trans>
            </text>
            <text x="164" y="46" fontSize="13" className="mag-snore mag-snore-2">
              <Trans>z</Trans>
            </text>
            <text x="174" y="32" fontSize="9" className="mag-snore mag-snore-3">
              <Trans>z</Trans>
            </text>
          </g>
        </>
      )}
      {mood === 'casting' && (
        <>
          {/* Concentration arcs — pulse with the cast sway so the squint
              reads as active, not frozen. */}
          <g
            className="mag-cast-eyes"
            stroke={eyeFill}
            strokeWidth="3.6"
            fill="none"
            strokeLinecap="round"
          >
            <path d="M46 112 Q58.6 96 71 112" />
            <path d="M114 112 Q127 96 140 112" />
          </g>
          {/* Floating sparkles above the right ear — three little ✦
              marks fading and drifting upward, staggered like the
              z-z-z snore pattern but for thinking. */}
          <g
            fill={eyeFill}
            fontFamily="'Geist', sans-serif"
            fontWeight={800}
            style={{ letterSpacing: '-0.04em' }}
          >
            <text x="150" y="62" fontSize="16" className="mag-cast-spark mag-cast-spark-1">
              ✦
            </text>
            <text x="164" y="46" fontSize="11" className="mag-cast-spark mag-cast-spark-2">
              ✦
            </text>
            <text x="174" y="32" fontSize="8" className="mag-cast-spark mag-cast-spark-3">
              ✦
            </text>
          </g>
        </>
      )}
      {mood === 'confused' && (
        <>
          {/* Left eye: original tall slit. Right eye: round dot. Asymmetry = puzzled. */}
          <g className="mag-eye">
            <ellipse cx="58.6097" cy="106.637" rx="11.3963" ry="17.0945" fill={eyeFill} />
            <ellipse cx="58.6101" cy="106.637" rx="3.2561" ry="12.2104" fill={pupil} />
          </g>
          <g className="mag-eye-2">
            <circle cx="126.988" cy="106.637" r="12" fill={eyeFill} />
            <circle cx="126.988" cy="106.637" r="5" fill={pupil} />
          </g>
        </>
      )}
      {mood === 'pounced' && (
        <>
          {/* Wide round excited eyes — wins / celebrations. */}
          <g className="mag-eye">
            <circle cx="58.6097" cy="106.637" r="14" fill={eyeFill} />
            <circle cx="58.6097" cy="106.637" r="6" fill={pupil} />
          </g>
          <g className="mag-eye-2">
            <circle cx="126.988" cy="106.637" r="14" fill={eyeFill} />
            <circle cx="126.988" cy="106.637" r="6" fill={pupil} />
          </g>
        </>
      )}
      {mood === 'smug' && (
        <>
          {/* Smug closed-eye crescents — gentle upward arcs like ‿‿,
              narrower than the napping shape so they read as a satisfied
              squint, not "asleep". Stroke-only so the eye reads as a
              shape, not a filled blob. */}
          <g stroke={eyeFill} strokeWidth="3.6" fill="none" strokeLinecap="round">
            <path d="M46 108 Q58.6 116 71 108" />
            <path d="M114 108 Q127 116 140 108" />
          </g>
          {/* Smirk — small asymmetric curve under the nose. Right side
              sits slightly higher than the left so it reads "told you
              so" rather than "pleased". */}
          <path
            d="M82 162 Q93 167 110 158"
            stroke={eyeFill}
            strokeWidth="2.6"
            fill="none"
            strokeLinecap="round"
          />
        </>
      )}
      {/* Nose */}
      <path d="M97.683 140.012H88.7287H86.2866L91.9848 149.882L97.683 140.012Z" fill={nose} />
      {/* Crescent-moon collar tag */}
      <circle cx="92.7987" cy="242.579" r="24.4207" fill={collarOuter} />
      <circle cx="100.125" cy="236.881" r="17.0945" fill={collarInner} />
    </svg>
  );
}

/**
 * 8-point sparkle glyph — handoff §3.4. Four rounded rects rotated at
 * 0/45/90/135° with a spell-yellow center dot. Used as a recurring brand
 * decoration (panel watermark, favicon, after-marks).
 */
export interface BrandSparkleProps {
  size?: number;
  /** Color of the rays. Center dot is always spell yellow. */
  color?: string;
  /** Optional outline color on each ray. Useful for small sizes
   *  (e.g. the 16px refresh-pill sparkle) where the fill alone fades
   *  into a light pill bg — a faint ink stroke makes the rays pop
   *  without raising fill saturation. Skip on the large auth-screen
   *  sparkle where the size is the affordance. */
  stroke?: string;
  /** Stroke width in pixels (non-scaling — same screen weight at 16px
   *  or 84px). Default 1.2 reads as a hairline. Ignored when `stroke`
   *  is unset. */
  strokeWidth?: number;
  /** Stroke opacity 0..1. Default 0.35 = "barely there, just enough to
   *  see the silhouette." Ignored when `stroke` is unset. */
  strokeOpacity?: number;
  className?: string;
  style?: CSSProperties;
}

export function BrandSparkle({
  size = 24,
  color = '#0F0E18',
  stroke,
  strokeWidth = 1.2,
  strokeOpacity = 0.35,
  className,
  style,
}: BrandSparkleProps) {
  const { t } = useLingui();
  // vector-effect:non-scaling-stroke keeps the outline at a constant
  // pixel width regardless of the SVG's render size — a stroke that
  // reads right at 16px also reads right at 84px.
  const rayProps = stroke
    ? {
        stroke,
        strokeWidth,
        strokeOpacity,
        vectorEffect: 'non-scaling-stroke' as const,
      }
    : {};
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      className={className}
      style={style}
      aria-hidden="true"
    >
      <title>{t`sparkle`}</title>
      <rect x="46" y="14" width="8" height="72" rx="4" fill={color} {...rayProps} />
      <rect
        x="46"
        y="14"
        width="8"
        height="72"
        rx="4"
        fill={color}
        transform="rotate(45 50 50)"
        {...rayProps}
      />
      <rect
        x="46"
        y="14"
        width="8"
        height="72"
        rx="4"
        fill={color}
        transform="rotate(90 50 50)"
        {...rayProps}
      />
      <rect
        x="46"
        y="14"
        width="8"
        height="72"
        rx="4"
        fill={color}
        transform="rotate(135 50 50)"
        {...rayProps}
      />
      <circle cx="50" cy="50" r="6" fill="#FFD018" />
    </svg>
  );
}
